<script setup lang="ts">
  import {onMounted, nextTick, ref} from 'vue';
  import useCpBem from '~/composables/useCpBem';
  import CpText from '~/components/cp/CpText/CpText.vue';
  import CpIconStandard from '~/components/cp/CpIcon/CpIconStandard/CpIconStandard.vue';
  import type {BreadCrumbItem} from '~/composables/useCatalogBreadCrumb';

  withDefaults(defineProps<{
    items: BreadCrumbItem[]
    noBorderBottom?: boolean
    alignItems?: 'left' | 'right'
  }>(), {
    alignItems: 'left',
    noBorderBottom: false,
  });

  defineEmits({
    breadCrumbEvent: null,
  });

  const {bm, e} = useCpBem('cp-breadcrumb');
  const breadcrumbEl = ref<HTMLElement>();

  onMounted(() => {
    nextTick(() => {
      if (breadcrumbEl.value && breadcrumbEl.value.scroll) {
        breadcrumbEl.value.scroll({top: 0, left: breadcrumbEl.value.scrollWidth, behavior: 'instant'});
      }
    });
  });
</script>

<template>
    <div ref="breadcrumbEl" :class="bm({ [alignItems]: true, noBorderBottom })">
        <div v-for="(item, i) in items" :key="i">
            <div v-if="(i + 1) !== items.length" :class="e('items-box')">
                <CpNuxtLink :to="item.value" :class="e`item`">
                    <div :class="e('tier')" @click="$emit('breadCrumbEvent', item.value)">
                        <CpText variant="caption-bold" :class="e('tier-text')">
                            {{ item.label }}
                        </CpText>
                    </div>
                </CpNuxtLink>
                <CpIconStandard icon="chevron_right" />
            </div>
            <CpText v-else :class="e('last')" variant="caption-regular" block>
                {{ item.label }}
            </CpText>
        </div>
    </div>
</template>

<style scoped lang="scss">
  .cp-breadcrumb {
    display: flex;
    align-items: center;
    border-bottom: var(--cp-border-standard);
    padding: 8px 16px;
    overflow-x: auto;
    overflow-y: hidden;
    text-wrap: nowrap;

    &--no-border-bottom {
      border-bottom: none;
    }

    &__tier {
      display: flex;
      border-radius: 16px;
      background-color: var(--background-color);
      padding: 4px 8px;

      &-text {
        color: var(--color-primary);
      }

      &:active {
        background-color: var(--background-color-active);
      }

      &:active &-text {
        color: var(--color-active);
      }
    }
    &__item {
      white-space: nowrap;
    }
    &__last {
      color: var(--color-last);
      cursor: default;
    }

    &:deep(.cp-icon-standard__font) {
      color: var(--color-last);
    }

    &__items-box {
      display: flex;
      align-items: center;
    }

    &--left {
      justify-content: flex-start;
    }

    &--right {
      justify-content: flex-end;
    }

    &__slider {
      display: flex;
      flex-direction: row;
      scrollbar-width: none;
      scroll-behavior: smooth;
      align-items: center;
      flex: 1 0 auto;
    }
  }

  .cp-breadcrumb::-webkit-scrollbar {
    height: 0;
    width: 0;
    display: none;
  }
</style>
